import { deltaCtMethod, deltaDeltaCtMethod } from '../statistics/qpcr';
import type { AnalysisOptions, ResultCache } from '../../stores/systems/keisuke/dashboardStore';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, KeisukeAdapterError, formatAdapterPValue } from '../keisukeAdapterI18n';

function buildQpcrDeltaCtPaperText(
  result: ReturnType<typeof deltaCtMethod>
): string {
  const methods =
    'Relative gene expression was quantified using the ΔCt method, normalizing target Ct values to housekeeping gene Ct values.';
  const results =
    `Mean relative expression = ${result.statistics.mean.toFixed(4)} (SD = ${result.statistics.sd.toFixed(4)}, SE = ${result.statistics.se.toFixed(4)}, n = ${result.samples.length}).`;
  return `Methods\n\n${methods}\n\nResults\n\n${results}`;
}

function buildQpcrDeltaDeltaCtPaperText(
  result: ReturnType<typeof deltaDeltaCtMethod>
): string {
  const methods =
    'Relative gene expression was quantified using the ΔΔCt method with the control group as reference. A two-sample t-test compared control and treatment groups on relative expression (log2 scale).';
  const tLine =
    result.tTest && result.tTest.pValue !== undefined
      ? ` t(${result.tTest.df}) = ${result.tTest.t.toFixed(2)}, ${formatAdapterPValue(result.tTest.pValue)}.`
      : '';
  const results =
    `Control mean relative expression = ${result.control.statistics.mean.toFixed(4)} (n = ${result.control.samples.length}); ` +
    `Treatment mean = ${result.treatment.statistics.mean.toFixed(4)} (n = ${result.treatment.samples.length}).${tLine}`;
  return `Methods\n\n${methods}\n\nResults\n\n${results}`;
}

function parseCtRow(
  row: DataRow,
  sampleCol: string,
  hkCol: string,
  targetCol: string
): { sampleName: string; housekeepingCt: number; targetCt: number } | null {
  const hk = parseFiniteNumberFromCell(row[hkCol]);
  const target = parseFiniteNumberFromCell(row[targetCol]);
  if (hk === null || target === null) return null;
  const sampleName = String(row[sampleCol] ?? '').trim() || 'Sample';
  return { sampleName, housekeepingCt: hk, targetCt: target };
}

function isControlSample(name: string): boolean {
  return /(control|ctrl|vehicle|対照|コントロール)/i.test(name);
}

function isTreatmentSample(name: string): boolean {
  return /(treat|treatment|drug|処理|投与)/i.test(name);
}

export function runQpcrAdapter(
  data: DataRow[],
  options: AnalysisOptions,
  gridHeaders: string[]
): ResultCache {
  const mapping = options.mapping ?? {};
  const sampleCol = mapping.groupColumn ?? gridHeaders[0] ?? 'sample';
  const hkCol = mapping.beforeColumn ?? gridHeaders[1] ?? 'housekeeping';
  const targetCol = mapping.valueColumn ?? gridHeaders[2] ?? 'target';
  const method = options.qpcrMethod ?? 'deltaCt';

  const rows = data
    .map((row) => parseCtRow(row, sampleCol, hkCol, targetCol))
    .filter((r): r is NonNullable<typeof r> => r !== null);

  if (rows.length === 0) {
    throw new KeisukeAdapterError('validCtRequired');
  }

  if (method === 'deltaCt') {
    const hkSamples = rows.map((r) => ({ sampleName: r.sampleName, ctValue: r.housekeepingCt }));
    const targetSamples = rows.map((r) => ({ sampleName: r.sampleName, ctValue: r.targetCt }));
    const result = deltaCtMethod(hkSamples, targetSamples);
    return {
      summary: kt('summary.qpcrDeltaCt', { n: result.samples.length }),
      stats: {
        method: 'deltaCt',
        meanRelativeExpression: result.statistics.mean,
        sd: result.statistics.sd,
        se: result.statistics.se,
      },
      statsDetailText: kt('statsDetail.qpcr.deltaCtLine', {
        mean: result.statistics.mean.toFixed(4),
      }),
      paperText: buildQpcrDeltaCtPaperText(result),
      updatedAt: Date.now(),
      rawResult: result,
      qpcrChart: {
        bars: result.samples.map((s) => ({
          label: s.sampleName,
          value: s.relativeExpression,
        })),
      },
    };
  }

  const control = rows.filter((r) => isControlSample(r.sampleName));
  const treatment = rows.filter((r) => isTreatmentSample(r.sampleName));
  if (control.length === 0 || treatment.length === 0) {
    throw new KeisukeAdapterError('qpcrControlTreatmentRequired');
  }
  const result = deltaDeltaCtMethod(
    control.map((r) => ({ sampleName: r.sampleName, ctValue: r.housekeepingCt })),
    control.map((r) => ({ sampleName: r.sampleName, ctValue: r.targetCt })),
    treatment.map((r) => ({ sampleName: r.sampleName, ctValue: r.housekeepingCt })),
    treatment.map((r) => ({ sampleName: r.sampleName, ctValue: r.targetCt }))
  );
  const pVal = result.tTest?.pValue;
  const significancePairs =
    pVal !== undefined
      ? [{ leftIndex: 0, rightIndex: 1, pValue: pVal }]
      : undefined;
  return {
    summary: kt('summary.qpcrDeltaDeltaCt', {
      controlN: control.length,
      treatmentN: treatment.length,
    }),
    stats: {
      method: 'deltaDeltaCt',
      controlMean: result.control.statistics.mean,
      treatmentMean: result.treatment.statistics.mean,
      pValue: pVal,
    },
    significancePairs,
    tTestPairwiseRow:
      pVal !== undefined && result.tTest
        ? {
            groupA: 'Control',
            groupB: 'Treatment',
            pValue: pVal,
            significant: pVal < 0.05,
          }
        : undefined,
    statsDetailText: kt('statsDetail.qpcr.deltaDeltaCtLine', {
      control: result.control.statistics.mean.toFixed(4),
      treatment: result.treatment.statistics.mean.toFixed(4),
    }),
    paperText: buildQpcrDeltaDeltaCtPaperText(result),
    updatedAt: Date.now(),
    rawResult: result,
    qpcrChart: {
      bars: [
        { label: 'Control', value: result.control.statistics.mean },
        { label: 'Treatment', value: result.treatment.statistics.mean },
      ],
    },
  };
}
